import React, { useState } from 'react'
import { Typography, Button, Collapse, IconButton, Tooltip } from '@material-ui/core'
import CircularProgress from '@material-ui/core/CircularProgress'
import ContentCopyIcon from '@material-ui/icons/ContentCopy'
import CheckIcon from '@material-ui/icons/Check'
import GetAppIcon from '@material-ui/icons/GetApp'
import { getDedupeValidationErrorFile } from '../../../../api/companyMigration'
import styled from 'styled-components'

const StyledErrorButton = styled(Button)`
  text-transform: none !important;
  background-color: #ffebee;
  color: #c62828;
  font-weight: 500;
  font-size: 0.5rem;
  height: 24px;
  border-radius: 4px;

  &:hover {
    background-color: #ffcdd2;
  }
`

const JsonContainer = styled.div`
  margin-top: 8px;
  background-color: #fff5f5;
  border: 1px solid #fed7d7;
  border-radius: 4px;
  overflow: hidden;
`

const JsonHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background-color: #ffebee;
  border-bottom: 1px solid #fed7d7;
`

const JsonContent = styled.div`
  max-height: 400px;
  overflow: auto;

  pre {
    margin: 0;
    padding: 12px;
    font-size: 0.75rem;
    font-family: 'Courier New', Courier, monospace;
    line-height: 1.5;
    white-space: pre-wrap;
    word-break: break-word;
    color: #333;

    .json-key {
      color: #881391;
      font-weight: 500;
    }

    .json-string {
      color: #1a1aa6;
    }

    .json-number {
      color: #1c00cf;
    }

    .json-boolean {
      color: #0d47a1;
    }

    .json-null {
      color: #9e9e9e;
    }
  }
`

const CopyButton = styled(IconButton)`
  && {
    padding: 4px;
    color: #c62828;

    &:hover {
      background-color: rgba(198, 40, 40, 0.08);
    }
  }
`

const syntaxHighlight = (json: string): string => {
  json = json.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  return json.replace(
    /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
    (match) => {
      let cls = 'json-number'
      if (/^"/.test(match)) {
        if (/:$/.test(match)) {
          cls = 'json-key'
        } else {
          cls = 'json-string'
        }
      } else if (/true|false/.test(match)) {
        cls = 'json-boolean'
      } else if (/null/.test(match)) {
        cls = 'json-null'
      }
      return '<span class="' + cls + '">' + match + '</span>'
    },
  )
}

const isValidationResponseJSON = (message: string): boolean => {
  try {
    const parsed = JSON.parse(message)
    return (
      (parsed?.validationResults && typeof parsed?.validationResults?.failed === 'number') ||
      parsed?.duplicateProducts
    )
  } catch {
    return false
  }
}

// Helper function to parse error messages
const parseErrorMessage = (
  message: string,
): { displayMessage: string; errorFilePath?: string; validationData?: any } => {
  if (isValidationResponseJSON(message)) {
    const validationData = JSON.parse(message)
    return {
      displayMessage: `Migration validation completed with ${
        validationData?.validationResults?.failed || validationData?.duplicateProducts?.length
      } error${
        validationData?.validationResults?.failed !== 1 ||
        validationData?.duplicateProducts?.length !== 1
          ? 's'
          : ''
      }`,
      validationData,
    }
  }

  const validationErrorRegex =
    /Product validation failed for (\d+) batches\. See auto-onboarding\/companies\/(\d+)\/products\/validation\/errors\/(.+\.json)/
  const match = message.match(validationErrorRegex)

  if (match) {
    const [, batchCount, companyId, filePath] = match
    return {
      displayMessage: `Product validation failed for ${batchCount} batches.`,
      errorFilePath: `companies/${companyId}/validation-errors/${filePath}`,
    }
  }

  return { displayMessage: message }
}

// Helper function to process and format error data
const processErrorData = (errorData: any): string => {
  try {
    // Extract and parse all error messages if they exist
    if (errorData?.errors && Array.isArray(errorData.errors)) {
      const parsedErrors = errorData.errors.map((errorItem: any, index: number) => {
        try {
          if (errorItem?.error?.message) {
            const messageStr = errorItem.error.message
            return {
              batch: errorItem.batch || index + 1,
              parsedMessage: JSON.parse(messageStr),
            }
          }
          return {
            batch: errorItem.batch || index + 1,
            rawError: errorItem,
          }
        } catch (parseError) {
          return {
            batch: errorItem.batch || index + 1,
            rawError: errorItem,
            parseError: 'Failed to parse message',
          }
        }
      })

      return JSON.stringify(
        {
          companyId: errorData.companyId,
          filename: errorData.filename,
          processedErrors: parsedErrors,
        },
        null,
        2,
      )
    }
    // Fallback to full error object if structure is different
    return JSON.stringify(errorData, null, 2)
  } catch (e) {
    // If parsing fails, show the raw data
    return JSON.stringify(errorData, null, 2)
  }
}

// Error Display Component
interface ErrorDisplayProps {
  message: string
}

export const ErrorDisplay: React.FC<ErrorDisplayProps> = ({ message }) => {
  const [showErrors, setShowErrors] = useState(false)
  const [errors, setErrors] = useState<any>(null)
  const [loadingErrors, setLoadingErrors] = useState(false)
  const [errorLoadFailed, setErrorLoadFailed] = useState(false)
  const [parsedErrors, setParsedErrors] = useState<any>(null)
  const [copied, setCopied] = useState(false)

  const { displayMessage, errorFilePath, validationData } = parseErrorMessage(message)

  const fetchErrors = async () => {
    if (!errorFilePath) return

    setLoadingErrors(true)
    setErrorLoadFailed(false)

    try {
      const errorData = await getDedupeValidationErrorFile({ fileUrl: errorFilePath })
      setErrors(errorData)
      setParsedErrors(processErrorData(errorData))
      setShowErrors(true)
    } catch (error) {
      console.error('Failed to fetch error details:', error)
      setErrorLoadFailed(true)
    } finally {
      setLoadingErrors(false)
    }
  }

  const toggleErrors = () => {
    if (validationData) {
      setShowErrors(!showErrors)
      if (!errors) {
        setErrors(validationData)
        setParsedErrors(JSON.stringify(validationData, null, 2))
      }
    } else if (!showErrors && !errors) {
      fetchErrors()
    } else {
      setShowErrors(!showErrors)
    }
  }

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(parsedErrors || '')
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      console.error('Failed to copy to clipboard:', error)
    }
  }

  const handleDownload = () => {
    try {
      const timestamp = new Date().toISOString().split('T')[0]
      let filename = `validation-errors-${timestamp}.json`
      if (validationData?.companyId) {
        filename = `validation-errors-company-${validationData.companyId}-${timestamp}.json`
      }
      const blob = new Blob([parsedErrors || ''], { type: 'application/json' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Failed to download file:', error)
    }
  }

  return (
    <div style={{ marginTop: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Typography variant="body2" color="error">
          {displayMessage}
        </Typography>
        {(errorFilePath || validationData) && (
          <StyledErrorButton onClick={toggleErrors} disabled={loadingErrors}>
            {loadingErrors ? <CircularProgress size={12} style={{ marginRight: 4 }} /> : null}
            {showErrors ? 'Hide errors' : 'Show errors'}
          </StyledErrorButton>
        )}
      </div>

      {errorLoadFailed && (
        <Typography variant="body2" color="error" style={{ marginTop: 4, fontSize: '0.875rem' }}>
          Failed to load error details. Please check the console or contact support.
        </Typography>
      )}

      <Collapse in={showErrors && !!errors}>
        <JsonContainer>
          <JsonHeader>
            <Typography variant="body2" style={{ fontSize: '0.75rem', fontWeight: 500 }}>
              Error Details
            </Typography>
            <div style={{ display: 'flex', gap: 4 }}>
              <Tooltip title="Download as JSON file">
                <CopyButton onClick={handleDownload}>
                  <GetAppIcon style={{ fontSize: 16 }} />
                </CopyButton>
              </Tooltip>
              <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
                <CopyButton onClick={handleCopyToClipboard}>
                  {copied ? (
                    <CheckIcon style={{ fontSize: 16 }} />
                  ) : (
                    <ContentCopyIcon style={{ fontSize: 16 }} />
                  )}
                </CopyButton>
              </Tooltip>
            </div>
          </JsonHeader>
          <JsonContent>
            <pre dangerouslySetInnerHTML={{ __html: syntaxHighlight(parsedErrors || '') }} />
          </JsonContent>
        </JsonContainer>
      </Collapse>
    </div>
  )
}



// WEBPACK FOOTER //
// ./src/containers/Settings/Development/MigrateCompanyProducts/ErrorDisplay.tsx